<script setup lang="ts">
import { SMARTCAPTCHA_CLIENT_KEY } from '~/shared/lib/smartCaptcha.const';
import {
  loadSmartCaptcha,
  type SmartCaptcha as SmartCaptchaApi,
} from '~/shared/lib/loadSmartCaptcha';

const token = defineModel<string>({ default: '' });

const container = ref<HTMLElement | null>(null);

let captcha: SmartCaptchaApi | null = null;
let widgetId: number | null = null;
let isUnmounted = false;

onMounted(async () => {
  try {
    captcha = await loadSmartCaptcha();
  } catch {
    // Without the widget the send is refused with the captcha line — nothing else to do here
    return;
  }

  if (isUnmounted || !container.value) return;

  widgetId = captcha.render(container.value, {
    sitekey: SMARTCAPTCHA_CLIENT_KEY,
    hl: 'ru',
    callback: (value) => {
      token.value = value;
    },
  });
  captcha.subscribe(widgetId, 'token-expired', () => {
    token.value = '';
  });
});

onBeforeUnmount(() => {
  isUnmounted = true;
  if (captcha && widgetId !== null) captcha.destroy(widgetId);
});

const reset = () => {
  token.value = '';
  if (captcha && widgetId !== null) captcha.reset(widgetId);
};

defineExpose({ reset });
</script>

<template>
  <div ref="container" :class="$style.captcha" />
</template>

<style module lang="scss">
// The widget's own height, held before it loads so the form does not jump
.captcha {
  width: 100%;
  max-width: 400px;
  min-height: 102px;
}

// A column that narrow leaves nothing to align to — the widget takes the width
@media (max-width: 599px) {
  .captcha {
    max-width: none;
  }
}
</style>
